import { CloseIcon, ConsentPreferences } from "@moznsystems/mozn-ui";
import type { CategoryDecision } from "@moznsystems/mozn-ui/consent";
import { type FC, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/modal/modal";
import {
	CONSENT_CATEGORY,
	type ConsentCategoryId,
} from "@/services/consent/config";

type CookiePreferencesModalProps = {
	categoryIds: ConsentCategoryId[];
	isAccepted: (categoryId: string) => boolean;
	onAcceptAll: () => void;
	onRejectNonEssential: () => void;
	onSave: (decision: CategoryDecision) => void;
	onClose: () => void;
};

const CookiePreferencesModal: FC<CookiePreferencesModalProps> = ({
	categoryIds,
	isAccepted,
	onAcceptAll,
	onRejectNonEssential,
	onSave,
	onClose,
}) => {
	const { t } = useTranslation("layout");
	const titleId = useId();
	const dialogRef = useRef<HTMLDivElement>(null);
	// Toggles are a draft: nothing is committed until one of the three actions.
	const [draft, setDraft] = useState<CategoryDecision>(() =>
		Object.fromEntries(categoryIds.map((id) => [id, isAccepted(id)])),
	);

	useEffect(() => {
		dialogRef.current?.focus();
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onClose]);

	const categories = useMemo(
		() =>
			categoryIds.map((id) => ({
				id,
				title: t(`cookie_consent.categories.${id}.title`),
				description: t(`cookie_consent.categories.${id}.description`),
				isRequired: id === CONSENT_CATEGORY.NECESSARY,
				isChecked: draft[id] ?? false,
			})),
		[categoryIds, draft, t],
	);

	return (
		<Modal>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				tabIndex={-1}
				className="flex w-full max-w-[520px] flex-col gap-4 outline-none"
				data-testid="cookie-preferences-modal"
			>
				<div className="flex items-start justify-between gap-4">
					<h2 id={titleId} className="m-0 font-bold text-[#222230] text-lg">
						{t("cookie_consent.preferences.title")}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label={t("cookie_consent.preferences.close")}
						className="flex cursor-pointer border-0 bg-transparent p-0"
						data-testid="cookie-preferences-modal-close"
					>
						<CloseIcon aria-hidden="true" />
					</button>
				</div>
				<ConsentPreferences
					description={t("cookie_consent.preferences.description")}
					categories={categories}
					onCategoryChange={(categoryId, isChecked) =>
						setDraft((prev) => ({ ...prev, [categoryId]: isChecked }))
					}
					acceptAllLabel={t("cookie_consent.preferences.accept_all")}
					rejectNonEssentialLabel={t(
						"cookie_consent.preferences.reject_non_essential",
					)}
					saveLabel={t("cookie_consent.preferences.save")}
					onAcceptAll={onAcceptAll}
					onRejectNonEssential={onRejectNonEssential}
					onSave={() => onSave(draft)}
					testId="cookie-preferences"
				/>
			</div>
		</Modal>
	);
};

export { CookiePreferencesModal };
export type { CookiePreferencesModalProps };
